import React, { useEffect, useState } from 'react';
import { AppState, Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useLocale } from '../i18n/LocaleContext';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { getNotifyPermissionStatus, requestNotifyPermission } from '../realtime/notifyIncomingMessage';
import { maybeNudgeFullScreenIntent, runAndroidNotificationsOnboardingOnce } from './androidNotifyOnboarding';
import { readNotifyPrimerSnooze, shouldShowNotifyPrimer, writeNotifyPrimerSnooze } from './notifyPrimerRules';

/**
 * Карточка-пояснение перед системным запросом уведомлений (аудит удобства 2026-10-04,
 * п. 34 и 15). Монтируется один раз после входа — вместо прежнего «спросить всё подряд».
 *
 * <p><b>Почему карточка, а не сразу системное окно.</b> Системное окно спрашивает «разрешить
 * уведомления?» без слова о том, зачем, а отказ в нём на Android 13+ и в браузерах — почти
 * навсегда: второй раз окно не появится. Карточка объясняет, что без уведомлений не будет
 * слышно звонков, и системное окно появляется только по нажатию «Включить».
 *
 * <p><b>Веб.</b> Браузеры разрешают запрос только из обработчика нажатия; запрос «сам», на
 * подключении сокета, они глушат и засчитывают как отказ. Здесь он ровно из нажатия.
 */
export function NotificationPermissionPrimer(): React.ReactElement | null {
  // Просьба про полноэкранные уведомления о звонке — после пропущенного звонка, при открытии
  // приложения: пропущенный приходит в фоне, где окно не показать (см. markMissedCallForNudge).
  useEffect(() => {
    void maybeNudgeFullScreenIntent();
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') void maybeNudgeFullScreenIntent();
    });
    return () => sub.remove();
  }, []);
  const { t } = useLocale();
  const { loginChrome } = useVisualTheme();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const status = await getNotifyPermissionStatus();
      if (status === 'denied') {
        // Выключены руками: системного окна больше не будет — одна подсказка про настройки.
        await runAndroidNotificationsOnboardingOnce();
        return;
      }
      const snoozedAt = await readNotifyPrimerSnooze();
      if (!cancelled && shouldShowNotifyPrimer(status, snoozedAt, Date.now())) setVisible(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  async function enable() {
    setBusy(true);
    try {
      await requestNotifyPermission();
    } finally {
      setBusy(false);
      setVisible(false);
    }
  }

  function later() {
    setVisible(false);
    void writeNotifyPrimerSnooze(Date.now());
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={later}>
      <View style={styles.backdrop}>
        <View
          style={[styles.card, { backgroundColor: loginChrome.cardBg, borderColor: loginChrome.inputBorder }]}
          accessibilityViewIsModal>
          <Text style={[styles.title, { color: loginChrome.titleColor }]} accessibilityRole="header">
            {t('notifyPrimerTitle')}
          </Text>
          <Text style={[styles.body, { color: loginChrome.subColor }]}>{t('notifyPrimerBody')}</Text>
          <Pressable
            style={[styles.primary, { backgroundColor: loginChrome.btnBg }, busy ? styles.busy : null]}
            onPress={() => void enable()}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel={t('notifyPrimerEnable')}>
            <Text style={styles.primaryText}>{t('notifyPrimerEnable')}</Text>
          </Pressable>
          <Pressable
            style={styles.secondary}
            onPress={later}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel={t('notifyPrimerLater')}>
            <Text style={[styles.secondaryText, { color: loginChrome.subColor }]}>{t('notifyPrimerLater')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
    padding: 16,
  },
  card: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    gap: 12,
    maxWidth: 480,
    width: '100%',
    alignSelf: 'center',
  },
  title: { fontSize: 18, fontWeight: '700' },
  body: { fontSize: 14, lineHeight: 20 },
  primary: { borderRadius: 12, minHeight: 48, alignItems: 'center', justifyContent: 'center' },
  primaryText: { color: '#fff', fontWeight: '600', fontSize: 16 },
  busy: { opacity: 0.6 },
  secondary: { minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  secondaryText: { fontSize: 15, fontWeight: '500' },
});
